/**
 * D-175, D-179: the one redo of an inaudible task, run like the exam: microphone test, then the
 * same subject, the examiner where the task has one (live, with its opening and its preparation),
 * the official time. A failure keeps the recording: it is sent again, or the task starts afresh;
 * only a recording received spends the redo. No exam frame here: D-176 is not concerned.
 */

import { academyAttemptsRedoCreate, academyAttemptsRedoRecordingCreate, academyAttemptsRedoTurnCreate } from "@agencyos/api-client";
import { clockText, flattenText, MIC_CHECK_LEVEL } from "@agencyos/core";
import { Button } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { Hourglass, Mic } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { closeStream, deviceVoices, extensionFor, openMicrophone, recorderMimeType, recorderOptions, sendRecording, speakFrench, useMicLevel, useMicStayedSilent, useSilence, utter } from "./audio";
import { speakEnglish } from "./ielts/speech";
import { type TurnPost, useLiveExaminer } from "./liveExaminer";

type Phase = "offer" | "mic" | "opening" | "prep" | "record" | "send" | "sent" | "error";

interface RedoInfo {
  prompt: string;
  exam: string;
  documents: unknown;
  material: unknown;
  live: boolean;
  follow_ups: string[];
  prep_seconds: number;
  seconds: number;
}

async function speak(exam: string, text: string): Promise<void> {
  if (exam === "ielts_general") return speakEnglish(text);
  if (exam === "goethe") {
    const voices = (await deviceVoices()).filter((voice) => voice.lang.toLowerCase().startsWith("de"));
    await utter(text, { lang: "de-DE", voice: voices[0], rate: 0.95 });
    return;
  }
  return speakFrench(text);
}

function useCountdown(seconds: number, running: boolean, onEnd: () => void): number {
  const [left, setLeft] = useState(seconds);
  const end = useRef(onEnd);
  end.current = onEnd;
  useEffect(() => {
    if (!running) return;
    setLeft(seconds);
    const until = Date.now() + seconds * 1000;
    const timer = window.setInterval(() => {
      const rest = (until - Date.now()) / 1000;
      setLeft(Math.max(rest, 0));
      if (rest <= 0) {
        window.clearInterval(timer);
        end.current();
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, [seconds, running]);
  return left;
}

export function RedoTask({ attemptId, productionId }: { attemptId: string; productionId: string }) {
  const { t } = useTranslation();
  const queries = useQueryClient();
  const [phase, setPhase] = useState<Phase>("offer");
  const [info, setInfo] = useState<RedoInfo | null>(null);
  const [prep, setPrep] = useState(0);
  const [extra, setExtra] = useState<string[]>([]);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [heard, setHeard] = useState(false);
  const [message, setMessage] = useState("");
  const [followUp, setFollowUp] = useState("");
  const level = useMicLevel(stream);
  const micSilent = useMicStayedSilent(level, phase === "record");
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const pending = useRef<Blob | null>(null);
  const followIndex = useRef(0);

  useEffect(() => () => closeStream(stream), [stream]);
  useEffect(() => {
    if (level > MIC_CHECK_LEVEL) setHeard(true);
  }, [level]);

  const open = async () => {
    setMessage("");
    try {
      setStream(await openMicrophone());
      setPhase("mic");
    } catch {
      setMessage(t("academy.redo.micDenied"));
    }
  };

  const upload = useCallback(
    async (blob: Blob) => {
      pending.current = blob;
      setPhase("send");
      const type = blob.type || "audio/webm";
      const audio = new File([blob], `redo.${extensionFor(type)}`, { type });
      try {
        const sent = await sendRecording(() => academyAttemptsRedoRecordingCreate(attemptId, { production: productionId, audio }));
        if (sent.status !== 200) throw new Error("refused");
        pending.current = null;
        setStream(null);
        setPhase("sent");
        void queries.invalidateQueries();
      } catch {
        setPhase("error");
      }
    },
    [attemptId, productionId, queries],
  );

  const record = useCallback(() => {
    if (!stream) return;
    const mimeType = recorderMimeType();
    const media = new MediaRecorder(stream, recorderOptions(mimeType));
    chunks.current = [];
    media.ondataavailable = (event) => {
      if (event.data.size) chunks.current.push(event.data);
    };
    media.onstop = () => void upload(new Blob(chunks.current, { type: media.mimeType || mimeType || "audio/webm" }));
    recorder.current = media;
    media.start(1000);
    setPhase("record");
  }, [stream, upload]);

  const stop = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }, []);

  const post = useCallback<TurnPost>((id, body) => academyAttemptsRedoTurnCreate(id, { production: productionId, audio: body.audio, offset: body.offset }), [productionId]);
  const examinerTask = useMemo(() => (info?.live ? { id: productionId, follow_ups: info.follow_ups } : undefined), [info, productionId]);
  const exam = info?.exam ?? "tcf_canada";
  const speakExam = useCallback((text: string) => speak(exam, text), [exam]);
  const live = useLiveExaminer({ attemptId, task: examinerTask, stream, level, active: phase === "record" && Boolean(info?.live), onEnd: stop, post, speak: speakExam });

  // No live examiner: the recorded follow-ups, when the learner stays silent, as in the exam.
  useSilence(level, phase === "record" && !info?.live && (info?.follow_ups.length ?? 0) > 0, 8, () => {
    const lines = info?.follow_ups ?? [];
    const line = lines[followIndex.current % lines.length] ?? "";
    followIndex.current += 1;
    setFollowUp(line);
    void speak(exam, line);
  });

  const begin = async (restart = false) => {
    setMessage("");
    pending.current = null;
    let answer;
    try {
      answer = await academyAttemptsRedoCreate(attemptId, { production: productionId, restart });
    } catch {
      setMessage(t("academy.redo.closed"));
      return;
    }
    if (answer.status !== 200) {
      setMessage(t("academy.redo.closed"));
      return;
    }
    const data = answer.data as unknown as RedoInfo;
    setInfo(data);
    setExtra([...flattenText(data.documents), ...flattenText(data.material)]);
    setPhase("opening");
    let wait = data.prep_seconds;
    // TCF task 2 and the tasks without examiner: the subject is read, as in the exam; the others:
    // the examiner opens the part (IELTS Part 2: its cue card and its minute of preparation).
    if (data.live && data.exam !== "tcf_canada") {
      try {
        const opened = (await academyAttemptsRedoTurnCreate(attemptId, { production: productionId })).data;
        if (opened.cue_card) setExtra((rows) => [...rows, ...flattenText(opened.cue_card)]);
        wait = opened.prep_until ? Math.max((new Date(opened.prep_until).getTime() - Date.now()) / 1000, 0) : 0;
        await speak(data.exam, opened.reply);
      } catch {
        wait = 0; // the task goes on: the learner has the subject on screen
      }
    } else {
      await speak(data.exam, data.prompt);
    }
    setPrep(wait);
    if (wait > 0) setPhase("prep");
    else setPhase("record");
  };

  // The recorder starts once the phase is set, with the callbacks of that render.
  const started = useRef(false);
  useEffect(() => {
    if (phase !== "record") {
      started.current = false;
      return;
    }
    if (!started.current) {
      started.current = true;
      record();
    }
  }, [phase, record]);

  const prepLeft = useCountdown(prep, phase === "prep", () => setPhase("record"));
  const speakLeft = useCountdown(info?.seconds ?? 0, phase === "record", stop);

  if (phase === "sent") {
    return (
      <p className="ac-status ac-secondary" role="status">
        <Hourglass aria-hidden size={18} /> {t("academy.redo.sent")}
      </p>
    );
  }
  return (
    <div className="stack">
      {phase === "offer" && (
        <>
          <p>{t("academy.redo.offer")}</p>
          <Button onClick={() => void open()}>
            <Mic aria-hidden size={18} /> {t("academy.redo.start")}
          </Button>
        </>
      )}
      {phase === "mic" && (
        <>
          <p>{t("academy.redo.micCheck")}</p>
          <progress aria-label={t("academy.redo.level")} max={1} value={level} />
          <Button disabled={!heard} onClick={() => void begin()}>
            {heard ? t("academy.redo.ready") : t("academy.redo.waitingVoice")}
          </Button>
        </>
      )}
      {info && (phase === "opening" || phase === "prep" || phase === "record") && (
        <>
          <p className="ac-production-text">{info.prompt}</p>
          {extra.map((line, index) => (
            <p key={index} className="ac-note">
              {line}
            </p>
          ))}
        </>
      )}
      {phase === "prep" && (
        <p className="ao-num" role="timer">
          {t("academy.redo.prep", { time: clockText(prepLeft) })}
        </p>
      )}
      {phase === "record" && (
        <>
          <p className="ao-num" role="timer">
            {t("academy.redo.recording", { time: clockText(speakLeft) })}
          </p>
          {micSilent && (
            <p className="ac-note ac-note--warning" role="alert">
              {t("academy.redo.micSilent")}
            </p>
          )}
          {followUp && <p className="ac-note">{followUp}</p>}
          {live.lines.map((line, index) => (
            <p key={index} className={`ac-bubble ac-bubble--${line.role === "learner" ? "me" : "them"}`}>
              {line.text}
            </p>
          ))}
          {live.offline && (
            <>
              <p className="ac-note">{t("academy.redo.examinerOffline")}</p>
              <Button
                variant="secondary"
                onClick={() => {
                  if (recorder.current?.state === "recording") {
                    recorder.current.onstop = null;
                    recorder.current.stop();
                  }
                  void begin(true);
                }}
              >
                {t("academy.redo.startAfresh")}
              </Button>
            </>
          )}
          {info?.live && (
            <Button disabled={live.status !== "listening"} onClick={live.finishTurn}>
              {t("academyExaminer.finishedSentence")}
            </Button>
          )}
          <Button variant="secondary" onClick={stop}>
            {t("academy.redo.finish")}
          </Button>
        </>
      )}
      {phase === "send" && <p role="status">{t("academy.redo.sending")}</p>}
      {phase === "error" && (
        <>
          <p className="ko" role="alert">
            {t("academy.redo.sendFailed")}
          </p>
          {pending.current && <Button onClick={() => void upload(pending.current as Blob)}>{t("academy.redo.sendAgain")}</Button>}
          <Button variant="secondary" onClick={() => void begin(true)}>
            {t("academy.redo.startAfresh")}
          </Button>
        </>
      )}
      {message && (
        <p className="ko" role="alert">
          {message}
        </p>
      )}
    </div>
  );
}
